/**
 * DM Listener Loop.
 *
 * Connects to the WebSocket, listens for incoming DMs, decrypts them,
 * routes to an orchestrator, encrypts the reply, and sends it back.
 *
 * Only processes messages from the agent's parent user (owner).
 */

import type { ArbiClient } from '@arbidocs/client'
import type { WebSocketServerMessage, WsNotificationResponse } from '@arbidocs/client'
import { connectWithReconnect, type ReconnectableWsConnection } from '../websocket.js'
import {
  type DmCryptoContext,
  decryptDmContent,
  listDMs,
  sendEncryptedDM,
} from '../operations/dm.js'
import type { Orchestrator } from './orchestrator.js'

export interface DmListenerOptions {
  /** Authenticated ArbiClient instance. The WebSocket token and re-login are
   *  both derived from this — it must already hold a valid session. */
  arbi: ArbiClient
  /** Base URL of the ARBI server */
  baseUrl: string
  /** DM crypto context for encrypt/decrypt */
  crypto: DmCryptoContext
  /** Orchestrator to route prompts to */
  orchestrator: Orchestrator
  /** Parent user ext_id — only process messages from this sender */
  parentExtId: string
  /** Called when a log-worthy event happens (verbose output) */
  onLog?: (message: string) => void
  /** Called when an error occurs during processing */
  onError?: (message: string, error?: unknown) => void
}

export interface DmListener {
  /** Stop listening and clean up */
  close(): void
}

/**
 * Start listening for DMs and routing them to the orchestrator.
 *
 * Connects via WebSocket with auto-reconnect. For each incoming DM:
 * 1. Filters to parent user only
 * 2. Decrypts the message
 * 3. Sends plaintext to orchestrator.prompt()
 * 4. Encrypts the response
 * 5. Sends encrypted reply via DM
 */
export async function startDmListener(options: DmListenerOptions): Promise<DmListener> {
  const { arbi, baseUrl, crypto, orchestrator, parentExtId, onLog, onError } = options

  const log = onLog ?? (() => {})
  const logError = onError ?? ((msg: string) => console.error(msg))

  const initialToken = arbi.session.getState().accessToken
  if (!initialToken) {
    throw new Error('No access token — run `arbi login` first.')
  }

  const handleNotification = async (msg: WsNotificationResponse) => {
    // Only process user_message type
    if (msg.type !== 'user_message') return

    // Only process messages from the parent user
    const senderExtId = msg.sender?.external_id
    if (!senderExtId || senderExtId !== parentExtId) {
      log(`Ignoring message from ${senderExtId ?? 'unknown'} (not parent)`)
      return
    }

    // Decrypt the message
    let plaintext: string | null
    try {
      plaintext = await decryptDmContent(arbi, msg, crypto)
    } catch (err) {
      logError('Failed to decrypt incoming DM', err)
      return
    }

    if (!plaintext) {
      log('Received empty message, skipping')
      return
    }

    log(`Received prompt from ${msg.sender?.email ?? senderExtId}: ${plaintext.slice(0, 80)}...`)

    // Route to orchestrator
    let response: string
    try {
      response = await orchestrator.prompt(plaintext, {
        senderExtId,
        senderEmail: msg.sender?.email ?? undefined,
      })
    } catch (err) {
      logError(`Orchestrator "${orchestrator.name}" failed`, err)
      response = `[Error: orchestrator failed — ${err instanceof Error ? err.message : String(err)}]`
    }

    log(`Orchestrator response (${response.length} chars): ${response.slice(0, 80)}...`)

    // Send encrypted reply back to the sender
    const senderPubKey = msg.sender?.encryption_public_key
    if (!senderPubKey) {
      logError('Cannot reply — sender has no encryption public key')
      return
    }

    try {
      await sendEncryptedDM(
        arbi,
        [
          {
            recipient_ext_id: senderExtId,
            content: response,
            recipient_encryption_public_key: senderPubKey,
          },
        ],
        crypto
      )
      log(`Reply sent to ${msg.sender?.email ?? senderExtId}`)
    } catch (err) {
      logError('Failed to send encrypted reply', err)
    }
  }

  // Backfill any unread DMs from the parent that landed while the
  // listener was offline (or in the WS-subscription race window during
  // startup). Without this, a message dispatched at the same second the
  // listener boots is silently lost.
  try {
    const unreadFromParent = (await listDMs(arbi)).filter(
      (n) => n.type === 'user_message' && n.read === false && n.sender?.external_id === parentExtId
    )
    if (unreadFromParent.length > 0) {
      log(`Backfilling ${unreadFromParent.length} unread DM(s) from parent`)
      for (const n of unreadFromParent) {
        await handleNotification(n as WsNotificationResponse).catch((err) =>
          logError('Backfill handler error', err)
        )
      }
    }
  } catch (err) {
    logError('Failed to backfill unread DMs', err)
  }

  // Connect WebSocket with auto-reconnect. This is an unattended daemon, so it
  // must reconnect forever (maxRetries: Infinity) rather than giving up after a
  // fixed number of tries — a multi-minute backend blip should not permanently
  // kill the listener. The token is read live from the session on every attempt,
  // and an auth-rejected reconnect triggers a re-login, so an expired token
  // self-heals instead of wedging the loop.
  const ws: ReconnectableWsConnection = await connectWithReconnect({
    baseUrl,
    accessToken: initialToken,
    getAccessToken: () => arbi.session.getState().accessToken,
    refreshAuth: () => arbi.auth.relogin(),
    maxRetries: Infinity,
    onMessage: (msg: WebSocketServerMessage) => {
      // Notifications have sender + recipient fields; filter to user_message type
      if ('sender' in msg && 'recipient' in msg) {
        const notification = msg as WsNotificationResponse
        if (notification.type === 'user_message') {
          handleNotification(notification).catch((err) =>
            logError('Notification handler error', err)
          )
        }
      }
    },
    onReconnecting: (attempt, max) => {
      const limit = Number.isFinite(max) ? `/${max}` : ''
      log(`WebSocket reconnecting (attempt ${attempt}${limit})...`)
    },
    onReconnected: () => {
      log('WebSocket reconnected')
    },
    onClose: (_code, reason) => {
      log(`WebSocket closed: ${reason || 'no reason'}`)
    },
  })

  log(`Listening for DMs (orchestrator: ${orchestrator.name}, parent: ${parentExtId})`)

  return {
    close() {
      ws.close()
      orchestrator.close?.()
    },
  }
}
